import React, { useState, useRef } from 'react';
import type { View } from '../../types';
import { ChevronLeftIcon, UploadIcon, TrashIcon } from '../icons';
import { useCreateGallery } from '../../lib/api/hooks/gallery';
import { useFileUpload } from '../../lib/api/hooks/file-upload';
import { showToast } from '../../lib/toast';

interface PhotoUploaderProps {
  folderId: string;
  setView: (view: View) => void;
}

interface UploadingFile {
    id: string;
    file: File;
    preview: string;
    progress: number;
}

const PhotoUploader: React.FC<PhotoUploaderProps> = ({ folderId, setView }) => {
    const [title, setTitle] = useState('');
    const [files, setFiles] = useState<UploadingFile[]>([]);
    const [isKeyHighlight, setKeyHighlight] = useState(false);
    const [isMultimedia, setMultimedia] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const createGalleryMutation = useCreateGallery();
    const uploadFileMutation = useFileUpload();

    const handleFileSelect = (selectedFiles: FileList | null) => {
        if (!selectedFiles) return;

        const newFiles: UploadingFile[] = Array.from(selectedFiles)
            .filter(file => file.type.startsWith('image/'))
            .map(file => ({
                id: `${Date.now()}-${file.name}`,
                file,
                preview: URL.createObjectURL(file),
                progress: 0
            }));

        if (newFiles.length === 0) {
            showToast.error('Please select valid image files (PNG, JPEG, JPG)');
            return;
        }

        setFiles(prev => [...prev, ...newFiles]);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const removeFile = (id: string) => {
        setFiles(prevFiles => {
            const fileToRemove = prevFiles.find(f => f.id === id);
            if (fileToRemove) {
                URL.revokeObjectURL(fileToRemove.preview);
            }
            return prevFiles.filter(f => f.id !== id);
        });
    };

    const handlePublish = async (status: 'DRAFT' | 'PUBLISH') => {
        if (!title.trim()) {
            showToast.error('Please enter a title');
            return;
        }

        if (files.length === 0) {
            showToast.error('Please select at least one photo');
            return;
        }

        setIsUploading(true);
        const uploadedUrls: string[] = [];

        try {
            // Step 1: Upload each file to image uploader and collect URLs
            for (let i = 0; i < files.length; i++) {
                const fileItem = files[i];

                // Update progress to show upload starting
                setFiles(prevFiles =>
                    prevFiles.map((f, idx) =>
                        idx === i ? { ...f, progress: 10 } : f
                    )
                );

                // Upload file to image uploader service
                const uploadResult = await uploadFileMutation.mutateAsync({
                    file: fileItem.file,
                    category: 'IMAGE'
                });

                if (!uploadResult.success || !uploadResult.data?.fileUrl) {
                    throw new Error(`Failed to upload ${fileItem.file.name}`);
                }

                // Store the URL
                uploadedUrls.push(uploadResult.data.fileUrl);

                // Update progress to 100% for this file
                setFiles(prevFiles =>
                    prevFiles.map((f, idx) =>
                        idx === i ? { ...f, progress: 100 } : f
                    )
                );
            }

            // Step 2: Create gallery with uploaded photo URLs
            const galleryData = {
                folderId,
                title: title.trim(),
                photoUrls: uploadedUrls,
                isKeyHighlight,
                isMultimedia,
                status
            };

            const result = await createGalleryMutation.mutateAsync(galleryData);

            if (result.success) {
                showToast.success(`Gallery ${status === 'PUBLISH' ? 'published' : 'saved as draft'} successfully`);

                // Small delay to show completion
                setTimeout(() => {
                    // Clean up object URLs
                    files.forEach(file => URL.revokeObjectURL(file.preview));

                    // Navigate back to folder view
                    setView({ page: 'Photo Gallery', subPage: 'folder', id: folderId });
                }, 500);
            } else {
                throw new Error('Gallery creation failed');
            }
        } catch (error) {
            console.error('Error uploading gallery:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to upload gallery';
            showToast.error(errorMessage);

            // Reset progress on error
            setFiles(prevFiles =>
                prevFiles.map(file => ({ ...file, progress: 0 }))
            );
        } finally {
            setIsUploading(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <header className="p-4 flex justify-between items-center border-b border-gray-200">
                <button
                    onClick={() => setView({ page: 'Photo Gallery', subPage: 'folder', id: folderId })}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
                    disabled={isUploading}
                >
                    <ChevronLeftIcon className="w-5 h-5" />
                    Go back
                </button>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => handlePublish('DRAFT')}
                        disabled={isUploading}
                        className="text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUploading ? 'Saving...' : 'Save as Draft'}
                    </button>
                    <button
                        onClick={() => handlePublish('PUBLISH')}
                        disabled={isUploading}
                        className="text-sm font-semibold text-white bg-[#047857] rounded-lg px-4 py-2 hover:bg-[#059669] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUploading ? 'Publishing...' : 'Publish'}
                    </button>
                </div>
            </header>

            <div className="p-6 md:p-10 max-w-2xl mx-auto space-y-6">
                <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                        Title
                    </label>
                    <input
                        type="text"
                        id="title"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Enter gallery title"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#047857] focus:border-transparent"
                        disabled={isUploading}
                    />
                </div>

                <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    className={`text-center border-2 border-dashed rounded-lg p-10 cursor-pointer transition-colors ${
                        isDragOver
                            ? 'border-[#047857] bg-green-50'
                            : 'border-gray-300 hover:border-gray-400'
                    } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <UploadIcon className="w-12 h-12 mx-auto text-gray-400" />
                    <p className="mt-2 font-semibold text-gray-700">
                        {isDragOver ? 'Drop files here' : 'Drag files to upload'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        Supported formats: PNG, JPEG, JPG (max 10MB per file)
                    </p>
                    <button
                        type="button"
                        className="mt-4 text-sm font-semibold text-[#047857] hover:underline"
                        disabled={isUploading}
                    >
                        Browse Files
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => handleFileSelect(e.target.files)}
                        className="hidden"
                        disabled={isUploading}
                    />
                </div>

                {files.length > 0 && (
                    <div className="space-y-4">
                        {files.map(file => (
                            <div key={file.id} className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg">
                               <div className="w-12 h-12 bg-gray-200 rounded-md flex-shrink-0 overflow-hidden">
                                   <img
                                       src={file.preview}
                                       alt="thumbnail"
                                       className="w-full h-full object-cover"
                                   />
                               </div>
                               <div className="flex-1 min-w-0">
                                   <div className="flex justify-between items-center gap-2">
                                        <p className="text-sm font-medium text-gray-800 truncate">{file.file.name}</p>
                                        <p className="text-sm text-gray-500 flex-shrink-0">{formatFileSize(file.file.size)}</p>
                                   </div>
                                   {file.progress > 0 && (
                                       <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                           <div
                                               className="bg-[#047857] h-1.5 rounded-full transition-all duration-300"
                                               style={{width: `${file.progress}%`}}
                                           ></div>
                                       </div>
                                   )}
                               </div>
                               <button
                                   onClick={(e) => {
                                       e.stopPropagation();
                                       removeFile(file.id);
                                   }}
                                   disabled={isUploading}
                                   className="p-2 text-red-500 hover:bg-red-50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                               >
                                   <TrashIcon className="w-5 h-5" />
                               </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <p className="font-semibold text-gray-800">Set Highlight</p>
                     <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isKeyHighlight}
                            onChange={e => setKeyHighlight(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                            disabled={isUploading}
                        />
                        <span className="ml-2 text-sm text-gray-700">Key Highlight</span>
                    </label>
                     <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isMultimedia}
                            onChange={e => setMultimedia(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                            disabled={isUploading}
                        />
                        <span className="ml-2 text-sm text-gray-700">Multimedia</span>
                    </label>
                </div>
            </div>
        </div>
    );
};

export default PhotoUploader;
